/**
 * Contribution margin (dekningsbidrag) of sale order lines against the product's last purchase
 * price (LPP, the product's `cost_price`).
 *
 * This is the single source of the formula: the order editor, the saved order view and the add
 * product modal all go through these helpers, so the figures cannot drift between screens.
 *
 * Formula, ex-VAT and after the line discount:
 *   net unit price  = unit_price × (1 − discount / 100)
 *   margin per unit = net unit price − LPP
 *   margin (%)      = margin / net revenue × 100 (undefined when net revenue is 0)
 *   TODO: extra cost was excluded from this formula for now.
 */

export interface MarginLineInput {
    quantity: number;
    unitPrice: number;
    discount?: number | null;
    /** A missing LPP counts as 0 cost: the product simply has no purchase price recorded. */
    lastPurchasePrice?: number | null;
}

export interface LineMargin {
    lastPurchasePrice: number;
    marginPerUnit: number;
    marginPercentage: number | null;
}

export interface ContributionMarginSummary {
    netRevenue: number;
    purchaseCostTotal: number;
    contributionMargin: number;
    contributionMarginPercentage: number | null;
}

function netUnitPrice(unitPrice: number, discount?: number | null): number {
    return unitPrice * (1 - (discount ?? 0) / 100);
}

function marginPercentage(margin: number, revenue: number): number | null {
    return revenue !== 0 ? (margin / revenue) * 100 : null;
}

export function calculateLineMargin(line: Omit<MarginLineInput, "quantity">): LineMargin {
    const lastPurchasePrice = line.lastPurchasePrice ?? 0;
    const net = netUnitPrice(line.unitPrice, line.discount);
    const marginPerUnit = net - lastPurchasePrice;

    return {
        lastPurchasePrice,
        marginPerUnit,
        marginPercentage: marginPercentage(marginPerUnit, net),
    };
}

export function summarizeContributionMargin(lines: MarginLineInput[]): ContributionMarginSummary {
    let netRevenue = 0;
    let purchaseCostTotal = 0;

    lines.forEach((line) => {
        netRevenue += netUnitPrice(line.unitPrice, line.discount) * line.quantity;
        purchaseCostTotal += (line.lastPurchasePrice ?? 0) * line.quantity;
    });

    const contributionMargin = netRevenue - purchaseCostTotal;

    return {
        netRevenue,
        purchaseCostTotal,
        contributionMargin,
        contributionMarginPercentage: marginPercentage(contributionMargin, netRevenue),
    };
}

export function formatMarginPercentage(value: number | null): string {
    return value === null ? "–" : `${value.toFixed(1)}%`;
}
